/**
 * Shared read model for the Fridge.com Refrigerator Capacity distribution.
 *
 * Single source of truth for the page (app/data/capacity-trends/page.tsx), the
 * JSON-LD schema props, the CSV route (app/data/capacity-trends/csv/route.ts),
 * and the share-card image route (app/data/capacity-trends/card/route.tsx), so
 * the on-page stat grid, the distribution figure, the segment tables, the
 * structured data, and every download cite the SAME figures from
 * app/data/_data/capacity-trends.json.
 *
 * INTEGRITY: every number here is read directly from that JSON, which is itself
 * a frozen snapshot of the capacity distribution derived from the live
 * Fridge.com catalog (active, listed models that publish a cubic-foot
 * capacity). Nothing is invented or narrated. A segment below the analyzed-model
 * minimum shows only its model count; its median and range are withheld rather
 * than published on a thin sample.
 */
import capacityData from '@/app/data/_data/capacity-trends.json'

/** One capacity-distribution segment (a type or a brand). */
export interface DistRow {
  /** Human-readable segment name. */
  label: string
  /** Analyzed models in the segment (published capacity, within the validity trim). */
  n: number
  p10: number
  p25: number
  median: number
  p75: number
  p90: number
  min: number
  max: number
}

export interface TypeRow extends DistRow {
  type: string
}

export interface BrandRow extends DistRow {
  brand: string
}

export interface BelowRow {
  type: string
  label: string
  n: number
}

export interface HistBand {
  lo: number
  hi: number
  n: number
}

export interface CapacityOverall {
  analyzed_models: number
  with_capacity: number
  active_catalog_models: number
  coverage_pct: number
  dropped_parse_errors: number
  min: number
  p10: number
  p25: number
  median: number
  p75: number
  p90: number
  max: number
  mean: number
  distinct_brands: number
  distinct_types: number
  types_published: number
  brands_published: number
  models_in_published_brands: number
  brands_below_count: number
  brands_below_models: number
  under5_n: number
  under5_pct: number
  full18_n: number
  full18_pct: number
  compact_cluster_median: number
  fullsize_cluster_median: number
}

export interface CapacityMeta {
  methodology_version: string
  computed_at: string
  source_program: string
  source_authority: string
  synthesis_note: string
  publication_min_n: number
  validity_cuft_max: number
  limitations: string[]
}

export const capacityMeta = capacityData.meta as unknown as CapacityMeta
export const capacityOverall = capacityData.overall as unknown as CapacityOverall
export const capacityTypeRows = capacityData.types as unknown as TypeRow[]
export const capacityTypesBelow = capacityData.types_below as unknown as BelowRow[]
/**
 * Brand rows carry `brand` in the JSON but no `label`, while the shared table,
 * the CSV route and the type rows all read `label`. Normalize once here so the
 * brand table's segment column and the CSV's `segment` cell carry the brand
 * name (before this the 37 brand rows rendered and exported blank).
 */
export const capacityBrandRows: BrandRow[] = (
  capacityData.brands as unknown as Array<Omit<BrandRow, 'label'> & { label?: string }>
).map((r) => ({ ...r, label: r.label ?? r.brand }))
export const capacityHistogram = capacityData.histogram as unknown as HistBand[]

export const capacityPublicationMinN = capacityMeta.publication_min_n ?? 8
export const capacityValidityMax = capacityMeta.validity_cuft_max ?? 40

/**
 * Format a cubic-foot figure for display: at most two decimals, trailing zeros
 * trimmed (16.02 → "16.02", 24.50 → "24.5", 21.00 → "21"). Truncation to two
 * decimals — a stated capacity is never rounded up to a whole number.
 */
export function formatCuft(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return String(parseFloat(v.toFixed(2)))
}

/** "12.20 ft³" style range label. */
export function formatCuftRange(lo: number | null, hi: number | null): string {
  if (lo === null || hi === null) return '—'
  return `${formatCuft(lo)}–${formatCuft(hi)}`
}

/** 'YYYY-MM-DD' → 'July 2026'. Parsed as UTC to avoid timezone drift. */
export function formatDateLabel(date: string | null | undefined): string {
  if (!date) return 'n/a'
  const [y, m] = date.split('-').map(Number)
  if (!y || !m) return date
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Public methodology label, e.g. "v1.0" (major.minor only). */
export const capacityMethodologyVersionLabel: string = (() => {
  const raw = capacityMeta.methodology_version ?? ''
  const core = raw.split('-')[0]
  const parts = core.split('.').filter(Boolean)
  if (parts.length >= 2) return `v${parts[0]}.${parts[1]}`
  return core ? `v${core}` : 'n/a'
})()

/** The k types with the largest median capacity, largest first. */
export function largestTypesByMedian(k: number): TypeRow[] {
  return [...capacityTypeRows].sort((a, b) => b.median - a.median).slice(0, k)
}

/** The k brands with the largest median capacity, largest first. */
export function largestBrandsByMedian(k: number): BrandRow[] {
  return [...capacityBrandRows].sort((a, b) => b.median - a.median).slice(0, k)
}

/** The k brands with the smallest median capacity, most compact first. */
export function mostCompactBrandsByMedian(k: number): BrandRow[] {
  return [...capacityBrandRows].sort((a, b) => a.median - b.median).slice(0, k)
}

/**
 * The largest- and smallest-median TYPE — the "runs biggest / runs smallest"
 * pair for the at-a-glance infographic. Both ends are published segments with
 * real samples; nothing is extrapolated.
 */
export function typeExtremes(): { large: TypeRow | null; small: TypeRow | null } {
  if (capacityTypeRows.length === 0) return { large: null, small: null }
  const sorted = [...capacityTypeRows].sort((a, b) => a.median - b.median)
  return { large: sorted[sorted.length - 1], small: sorted[0] }
}

/** The published segment with the widest P10–P90 spread (type or brand supplied). */
export function widestSpread(rows: DistRow[]): DistRow | null {
  if (rows.length === 0) return null
  return [...rows].sort((a, b) => b.p90 - b.p10 - (a.p90 - a.p10))[0]
}

/**
 * Canonical FAQ set for the capacity distribution, drawn from the live numbers.
 * Shared by the FAQPage JSON-LD and the on-page FAQ so the structured data
 * always matches the rendered text.
 */
export function buildCapacityFAQs(): Array<{ question: string; answer: string }> {
  const o = capacityOverall
  return [
    {
      question: 'What is the Fridge.com Refrigerator Capacity distribution?',
      answer: `It is a statistical summary of the published storage capacity — in cubic feet — of the refrigeration models Fridge.com carries. Across ${o.analyzed_models.toLocaleString('en-US')} active models that publish a capacity, the median is ${formatCuft(o.median)} cubic feet, with the middle half of the catalog (the 25th to 75th percentile) running from ${formatCuft(o.p25)} to ${formatCuft(o.p75)} cubic feet. The page breaks that distribution down by appliance type and by brand so you can see which segments run large and which run compact.`,
    },
    {
      question: 'How many models is this based on, and what share of the catalog publishes a capacity?',
      answer: `Of ${o.active_catalog_models.toLocaleString('en-US')} active listed models, ${o.with_capacity.toLocaleString('en-US')} publish a cubic-foot capacity — about ${o.coverage_pct}% of the catalog. After dropping ${o.dropped_parse_errors} values above ${capacityValidityMax} cubic feet as data-entry or parse errors, ${o.analyzed_models.toLocaleString('en-US')} models remain in the analyzed range of 0 to ${capacityValidityMax} cubic feet. Models without a published capacity are excluded, never estimated.`,
    },
    {
      question: 'Why does the overall median describe so few actual products?',
      answer: `Because the catalog is bimodal. A compact cluster of personal, beverage, and specialty units sits near ${formatCuft(o.compact_cluster_median)} cubic feet, and a full-size cluster of kitchen refrigerators sits near ${formatCuft(o.fullsize_cluster_median)} cubic feet. ${o.under5_n.toLocaleString('en-US')} models (${o.under5_pct}%) hold under 5 cubic feet, while ${o.full18_n.toLocaleString('en-US')} (${o.full18_pct}%) hold 18 or more. The single overall median of ${formatCuft(o.median)} cubic feet falls in the sparse valley between the two peaks, which is exactly why the distribution is shown by type and by brand rather than as one number.`,
    },
    {
      question: 'Which appliance types and brands run largest?',
      answer: `By type, French-door refrigerators carry the highest median capacity, followed by side-by-side and full-size refrigerators; compact and wine-cooler units carry the lowest. By brand, the full-size specialists run largest at the median, while the compact and specialty specialists run smallest. Every median on the page sits beside its own 10th-to-90th-percentile range, so a segment is never reduced to a single figure.`,
    },
    {
      question: 'Why do some types or brands show no figures?',
      answer: `A type or brand is shown only once it carries at least ${capacityPublicationMinN} analyzed models. ${o.types_published} of ${o.distinct_types} types and ${o.brands_published} of ${o.distinct_brands} brands clear that bar; thinner segments are disclosed by model count but their median and range are withheld rather than published on a small sample. A withheld figure means "not enough data," never zero.`,
    },
    {
      question: 'How current is this, and can I download it?',
      answer: `This release, methodology ${capacityMethodologyVersionLabel}, reflects the catalog as of ${formatDateLabel(capacityMeta.computed_at)}. The catalog changes, so the figures are a dated snapshot rather than a live feed. The full type-and-brand table is available as a CSV at https://fridge.com/data/capacity-trends/csv, and the method is documented on the page.`,
    },
  ]
}
